import { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

export interface NavDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  side?: 'left' | 'right';
  children: React.ReactNode;
  className?: string;
  showOverlay?: boolean;
}

export function NavDrawer({
  isOpen,
  onClose,
  side = 'left',
  children,
  className = '',
  showOverlay = true,
}: NavDrawerProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const slideFrom = side === 'left' ? '-translate-x-full' : 'translate-x-full';

  return (
    <>
      {showOverlay && isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600/75 transition-opacity duration-300"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <div
        className={`
          fixed top-0 bottom-0 z-50
          w-[280px] max-w-[85vw]
          bg-white shadow-xl
          transform transition-transform duration-300 ease-in-out
          ${side === 'left' ? 'left-0' : 'right-0'}
          ${isOpen ? 'translate-x-0' : slideFrom}
          ${className}
        `}
        role="dialog"
        aria-modal="true"
        aria-label={side === 'left' ? 'Navigation menu' : 'Menu'}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="h-full overflow-y-auto py-16 px-4">{children}</div>
      </div>
    </>
  );
}

export default NavDrawer;
